import React from 'react';
import MDBox from 'src/components/md/MDBox';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import { CircularProgress, IconButton } from '@mui/material';
import MDButton from 'src/components/md/MDButton';
import MDTypography from 'src/components/md/MDTypography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTable from 'src/md-examples/Tables/DataTable';
import { Column } from 'react-table';
import useCoreCustomFields, {
  CoreCustomFieldDTO,
} from 'src/api/core/core-custom-fields/useCoreCustomFields';

// Map technical model names to human-readable labels
const MODEL_NAME_MAP: Record<string, string> = {
  im_products: 'Product',
  im_products_v2: 'Product',
  im_unit_variations: 'Unit Variation',
  im_categories: 'Category',
  im_inventory_items: 'Inventory Item',
  ar_customers: 'Customer',
  ar_invoices: 'Invoice',
  ap_suppliers: 'Supplier',
  ap_internal_purchase_orders: 'Purchase Order',
  scm_shipments: 'Shipment',
  scm_work_orders: 'Work Order',
  scm_sales_orders: 'Sales Order',
  // Add more mappings as needed
};

const CoreCustomFieldsCard: React.FC = () => {
  const coreCustomFields = useCoreCustomFields({ includeEnum: true });

  const loading = coreCustomFields.isLoading;
  const error = coreCustomFields.error;

  const columns: Column<CoreCustomFieldDTO>[] = [
    { Header: 'Field Name', accessor: 'fieldName' },
    {
      Header: 'Model',
      accessor: 'model',
      Cell: ({ value }: { value: string }) => (
        <MDTypography
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '300px',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            color: 'inherit',
          }}
        >
          {MODEL_NAME_MAP[value] || value}
        </MDTypography>
      ),
    },
    {
      Header: 'Field Type',
      accessor: 'fieldType',
      Cell: ({
        value,
        row,
      }: {
        value: string;
        row: { original: CoreCustomFieldDTO };
      }) => {
        const isEnum = value === 'enum';
        const coreEnum = row.original.coreEnum;

        return (
          <MDTypography
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '300px',
              fontSize: 'inherit',
              fontWeight: 'inherit',
              color: 'inherit',
            }}
          >
            {value || '-'}
            {isEnum && coreEnum && (
              <MDTypography
                component="span"
                sx={{
                  ml: 1,
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  color: 'inherit',
                }}
              >
                ({coreEnum.name})
              </MDTypography>
            )}
            {isEnum && !coreEnum && (
              <MDTypography
                component="span"
                sx={{
                  ml: 1,
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  color: 'text.secondary',
                }}
              >
                (No enum assigned)
              </MDTypography>
            )}
          </MDTypography>
        );
      },
    },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ value }: { value: string }) => (
        <MDTypography
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '300px',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            color: 'inherit',
          }}
        >
          {value || '-'}
        </MDTypography>
      ),
    },
    {
      Header: 'Multiple',
      id: 'isMultiple',
      accessor: (row) => row.isMultiple,
      Cell: ({ value }: { value: boolean }) => (
        <MDTypography variant="caption">{value ? 'Yes' : 'No'}</MDTypography>
      ),
    },
    {
      Header: '',
      id: 'actions',
      Cell: ({ row }: { row: { original: CoreCustomFieldDTO } }) => (
        <MDBox display="flex" textAlign={'right'}>
          <IconButton
            onClick={() => console.log(row.original)}
            aria-label="edit"
            size="small"
            sx={{ opacity: 0.7, color: 'grey.600', '&:hover': { opacity: 1 } }}
            disabled
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            // onClick={() => handleDelete(row.original)}
            aria-label="delete"
            size="small"
            sx={{ opacity: 0.7, color: 'grey.600', '&:hover': { opacity: 1 } }}
            disabled
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </MDBox>
      ),
    },
  ];

  if (loading) {
    return (
      <MDBox pt={2} pb={3} px={3} display="flex" justifyContent="center">
        <CircularProgress />
      </MDBox>
    );
  } else if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <MDBox pt={2} pb={3} px={3}>
      {coreCustomFields.data?.edges.length === 0 ? (
        <MDBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          py={4}
        >
          <MDBox mb={2}>
            <MDBox mb={2}>
              <MDTypography variant="body2" color="text" sx={{ mt: 0, mb: 4 }}>
                {`It looks like there aren't any core custom fields created yet.`}
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox display="flex" alignItems="center">
            <MDButton
              variant="contained"
              color="primary"
              onClick={() => {
                // openCreateModal();
                console.log('Create core custom field');
              }}
            >
              Create
            </MDButton>
          </MDBox>
        </MDBox>
      ) : (
        <DataTable<CoreCustomFieldDTO>
          canSearch={false}
          table={{ columns, rows: coreCustomFields.data?.edges ?? [] }}
          pagination={{ variant: 'gradient', color: 'info' }}
          showTotalEntries={false}
          isSorted={false}
        />
      )}
    </MDBox>
  );
};

export default CoreCustomFieldsCard;
